/**
 * AsaasOnboardingPage — cria a subconta Asaas e acompanha o envio dos
 * documentos, contra o SANDBOX.
 *
 * ⚠️ ESTA É A TELA DE CADASTRO/CRIAÇÃO DE SUBCONTA QUE O PLAYBOOK DE BaaS
 * EXIGE COMO EVIDÊNCIA (#219) — e por isso ela leva o selo do Asaas mesmo
 * sendo um caminho de teste: a exigência regulatória (Resolução Conjunta
 * nº 16/2025) não distingue ambiente.
 *
 * Por que esta tela existe com o BaaS ainda CONGELADO em produção (addendum
 * do #165): o congelamento é do lançamento para o USUÁRIO FINAL — a
 * detecção automática de pagamento continua invisível para todo mundo
 * (`AUTO_PAYMENT_DETECTION_FROZEN` em `paymentDetection.ts`). Esta tela é o
 * CAMINHO DE TESTE que o #166/#208 já documentam (`plan_type = 'admin'`),
 * agora com uma UI própria em vez de só scripts — decisão explícita de
 * sessão de 2026-08-10, para permitir tirar os prints que o playbook exige
 * como evidência, sem tocar no ambiente de produção.
 *
 * O portão é DUPLO e nenhuma metade substitui a outra:
 *  1. `useAsaasAccount` exige `isAdmin` (owner/admin) — mesmo papel que o
 *     servidor (`requireCompanyAdmin`) cobra;
 *  2. `asaas-onboard` exige plano `admin`/override (`resolveEntitlement`) —
 *     ninguém chega aqui assinando o PRO comum.
 * NÃO reusa `autoDetectionReachable()`: aquela função existe para calar o
 * bloco de PRODUÇÃO enquanto o BaaS não é aprovado — aplicá-la aqui
 * tornaria esta tela permanentemente inalcançável, o que destruiria o
 * próprio propósito dela (testar/gerar evidência em sandbox).
 *
 * O ambiente operado é o que a Edge Function decidir (`ASAAS_ENVIRONMENT`):
 * sandbox por padrão, e produção só se o segredo estiver explicitamente
 * configurado — decisão que não é deste código, é do deploy.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ExternalLink, Loader2 } from 'lucide-react';
import { MainLayout } from '@/components/layout/MainLayout';
import { Button } from '@/components/ui/button';
import { Button3D } from '@/components/ui/button-3d';
import { FieldLabel, FIELD_INPUT, CONTENT_CARD } from '@/components/ui/field';
import { AsaasSeal } from '@/components/payments/AsaasSeal';
import { useAsaasAccount } from '@/hooks/useAsaasAccount';
import { formatCep, formatDocument, onlyDigits } from '@/lib/documentUtils';
import {
  ASAAS_DOCUMENT_STATUS_LABELS,
  EMPTY_ASAAS_ONBOARD_FORM,
  asaasDocumentIsCnpj,
  validateAsaasOnboardForm,
  type AsaasCompanyType,
  type AsaasOnboardFormValues,
} from '@/lib/asaasOnboarding';

const ACCOUNT_STATUS_LABELS: Record<string, string> = {
  pending: 'Em análise pelo Asaas',
  approved: 'Aprovada',
  disabled: 'Desabilitada',
};

/** Abre um link externo — web-only via `window.open` já é o padrão do app (#35h). */
function openExternal(url: string): void {
  window.open(url, '_blank', 'noopener,noreferrer');
}

export default function AsaasOnboardingPage() {
  const navigate = useNavigate();
  const {
    ready, account, accountLoading, documents, documentsRejectReasons,
    documentsLoading, documentsPending, creating, createAccount,
  } = useAsaasAccount();

  const [values, setValues] = useState<AsaasOnboardFormValues>(EMPTY_ASAAS_ONBOARD_FORM);
  const [errors, setErrors] = useState<ReturnType<typeof validateAsaasOnboardForm>['errors']>({});

  const isCnpj = asaasDocumentIsCnpj(values.cpfCnpj);

  const set = <K extends keyof AsaasOnboardFormValues>(key: K, value: AsaasOnboardFormValues[K]) =>
    setValues((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = async () => {
    const validation = validateAsaasOnboardForm(values);
    setErrors(validation.errors);
    if (!validation.ok) return;
    try {
      await createAccount(values);
    } catch {
      // O toast já saiu no `onError` da mutation.
    }
  };

  return (
    <MainLayout showFab={false}>
      <div className="space-y-5 pb-8">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} aria-label="Voltar">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Ambiente de teste (sandbox)
            </p>
            <h1 className="text-xl font-semibold">Conectar conta de pagamento</h1>
          </div>
        </div>

        {!ready ? (
          <div className={`${CONTENT_CARD} p-4 text-sm text-muted-foreground`}>
            Só o dono ou um administrador da empresa pode conectar a conta de pagamento.
          </div>
        ) : accountLoading ? (
          <div className={`${CONTENT_CARD} p-4 text-sm text-muted-foreground`}>Carregando…</div>
        ) : !account ? (
          <OnboardForm
            values={values}
            errors={errors}
            isCnpj={isCnpj}
            creating={creating}
            onChange={set}
            onSubmit={handleSubmit}
          />
        ) : (
          <div className="space-y-4">
            <div className={`${CONTENT_CARD} p-4`}>
              <p className="text-sm font-medium">
                Situação da conta: {ACCOUNT_STATUS_LABELS[account.status] ?? account.status}
              </p>
              {account.status_detail && (
                <p className="mt-1 text-xs text-muted-foreground">{account.status_detail}</p>
              )}
              {account.status === 'approved' && !account.pix_key_ready && (
                <p className="mt-1 text-xs text-amber-700 dark:text-amber-400">
                  A conta foi aprovada, mas ainda não pode gerar QR Pix — confira se os
                  documentos abaixo foram todos aprovados (#170).
                </p>
              )}
            </div>

            <div className={`${CONTENT_CARD} p-4`}>
              <p className="text-sm font-medium">Documentos</p>
              {documentsPending ? (
                <p className="mt-2 text-xs text-muted-foreground">
                  Aguarde alguns segundos — o Asaas ainda está validando o cadastro.
                </p>
              ) : documentsLoading ? (
                <p className="mt-2 text-xs text-muted-foreground">Carregando…</p>
              ) : documents.length === 0 ? (
                <p className="mt-2 text-xs text-muted-foreground">Nenhum documento pendente.</p>
              ) : (
                <ul className="mt-2 space-y-2">
                  {documents.map((group) => (
                    <li key={group.id} className="rounded-lg border border-border p-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-sm font-medium">{group.title}</p>
                          <p className="text-xs text-muted-foreground">{group.description}</p>
                        </div>
                        <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-[11px] font-semibold text-muted-foreground">
                          {ASAAS_DOCUMENT_STATUS_LABELS[group.status]}
                        </span>
                      </div>
                      {group.onboardingUrl && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="mt-2"
                          onClick={() => openExternal(group.onboardingUrl!)}
                        >
                          <ExternalLink className="mr-2 h-4 w-4" />
                          Enviar documento
                        </Button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
              {documentsRejectReasons && (
                <p className="mt-2 text-xs text-destructive">{documentsRejectReasons}</p>
              )}
            </div>
          </div>
        )}

        {/*
          Evidência que o playbook exige: o selo nesta tela de criação de
          conta/subconta. Fica fora dos ramos condicionais de propósito — vale
          antes, durante e depois da criação.
        */}
        <div className={`${CONTENT_CARD} p-4`}>
          <AsaasSeal />
        </div>
      </div>
    </MainLayout>
  );
}

interface OnboardFormProps {
  values: AsaasOnboardFormValues;
  errors: ReturnType<typeof validateAsaasOnboardForm>['errors'];
  isCnpj: boolean;
  creating: boolean;
  onChange: <K extends keyof AsaasOnboardFormValues>(key: K, value: AsaasOnboardFormValues[K]) => void;
  onSubmit: () => void;
}

function OnboardForm({ values, errors, isCnpj, creating, onChange, onSubmit }: OnboardFormProps) {
  return (
    <div className={`${CONTENT_CARD} space-y-3 p-4`}>
      <div>
        <FieldLabel htmlFor="asaas-name">Nome do titular</FieldLabel>
        <input
          id="asaas-name"
          className={`w-full px-3 ${FIELD_INPUT}`}
          value={values.name}
          onChange={(e) => onChange('name', e.target.value)}
        />
        {errors.name && <p className="mt-1 text-xs text-destructive">{errors.name}</p>}
      </div>

      <div>
        <FieldLabel htmlFor="asaas-email">E-mail</FieldLabel>
        <input
          id="asaas-email"
          type="email"
          className={`w-full px-3 ${FIELD_INPUT}`}
          value={values.email}
          onChange={(e) => onChange('email', e.target.value)}
        />
        {errors.email && <p className="mt-1 text-xs text-destructive">{errors.email}</p>}
      </div>

      <div>
        <FieldLabel htmlFor="asaas-doc">CPF ou CNPJ do titular</FieldLabel>
        <input
          id="asaas-doc"
          inputMode="numeric"
          className={`w-full px-3 ${FIELD_INPUT}`}
          value={formatDocument(values.cpfCnpj)}
          onChange={(e) => onChange('cpfCnpj', onlyDigits(e.target.value))}
        />
        {errors.cpfCnpj && <p className="mt-1 text-xs text-destructive">{errors.cpfCnpj}</p>}
      </div>

      {isCnpj ? (
        <div>
          <FieldLabel htmlFor="asaas-company-type">Tipo de empresa</FieldLabel>
          <select
            id="asaas-company-type"
            className={`w-full px-3 ${FIELD_INPUT}`}
            value={values.companyType}
            onChange={(e) => onChange('companyType', e.target.value as AsaasCompanyType)}
          >
            <option value="MEI">MEI</option>
            <option value="LIMITED">Limitada</option>
            <option value="INDIVIDUAL">Empresário individual</option>
            <option value="ASSOCIATION">Associação</option>
          </select>
        </div>
      ) : (
        <div>
          <FieldLabel htmlFor="asaas-birth">Data de nascimento do titular</FieldLabel>
          <input
            id="asaas-birth"
            type="date"
            className={`w-full px-3 ${FIELD_INPUT}`}
            value={values.birthDate}
            onChange={(e) => onChange('birthDate', e.target.value)}
          />
          {errors.birthDate && <p className="mt-1 text-xs text-destructive">{errors.birthDate}</p>}
        </div>
      )}

      <div>
        <FieldLabel htmlFor="asaas-phone">Celular (com DDD)</FieldLabel>
        <input
          id="asaas-phone"
          inputMode="numeric"
          className={`w-full px-3 ${FIELD_INPUT}`}
          value={values.mobilePhone}
          onChange={(e) => onChange('mobilePhone', e.target.value)}
        />
        {errors.mobilePhone && <p className="mt-1 text-xs text-destructive">{errors.mobilePhone}</p>}
      </div>

      <div>
        <FieldLabel htmlFor="asaas-cep">CEP</FieldLabel>
        <input
          id="asaas-cep"
          inputMode="numeric"
          className={`w-full px-3 ${FIELD_INPUT}`}
          value={formatCep(values.postalCode)}
          onChange={(e) => onChange('postalCode', onlyDigits(e.target.value))}
        />
        {errors.postalCode && <p className="mt-1 text-xs text-destructive">{errors.postalCode}</p>}
      </div>

      <div className="grid grid-cols-[1fr_auto] gap-2">
        <div>
          <FieldLabel htmlFor="asaas-address">Logradouro</FieldLabel>
          <input
            id="asaas-address"
            className={`w-full px-3 ${FIELD_INPUT}`}
            value={values.address}
            onChange={(e) => onChange('address', e.target.value)}
          />
          {errors.address && <p className="mt-1 text-xs text-destructive">{errors.address}</p>}
        </div>
        <div>
          <FieldLabel htmlFor="asaas-number">Número</FieldLabel>
          <input
            id="asaas-number"
            className={`w-20 px-3 ${FIELD_INPUT}`}
            value={values.addressNumber}
            onChange={(e) => onChange('addressNumber', e.target.value)}
          />
          {errors.addressNumber && <p className="mt-1 text-xs text-destructive">{errors.addressNumber}</p>}
        </div>
      </div>

      <div>
        <FieldLabel htmlFor="asaas-complement">Complemento (opcional)</FieldLabel>
        <input
          id="asaas-complement"
          className={`w-full px-3 ${FIELD_INPUT}`}
          value={values.complement}
          onChange={(e) => onChange('complement', e.target.value)}
        />
      </div>

      <div>
        <FieldLabel htmlFor="asaas-province">Bairro</FieldLabel>
        <input
          id="asaas-province"
          className={`w-full px-3 ${FIELD_INPUT}`}
          value={values.province}
          onChange={(e) => onChange('province', e.target.value)}
        />
        {errors.province && <p className="mt-1 text-xs text-destructive">{errors.province}</p>}
      </div>

      <Button3D disabled={creating} onClick={onSubmit} className="w-full">
        {creating ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Criando…
          </>
        ) : (
          'Criar subconta'
        )}
      </Button3D>
    </div>
  );
}
